import React, { useState, useEffect } from "react";
import { Icon } from "../shared/Icons";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "../../api";
import "./UpiPaymentModal.css";

// Import local logos
import gpayLogo from "../../assets/logos/gpay.png";
import phonepeLogo from "../../assets/logos/phonepe.png";
import paytmLogo from "../../assets/logos/paytm.png";
import bhimLogo from "../../assets/logos/bhim.png";

export function UpiPaymentModal({ amount, contact, onClose, onVerifyAndPay }) {
  const [upiId, setUpiId] = useState("");
  const [utr, setUtr] = useState("");
  const [phase, setPhase] = useState("input"); // 'input' | 'verifying'
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [publicSettings, setPublicSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(prev => prev > 0 ? prev - 1 : 0), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    (async () => {
       try {
         const s = await api("/api/settings/public", { auth: false });
         setPublicSettings(s);
       } catch {}
       setLoading(false);
    })();
  }, []);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleInitiate = () => {
    const upiLink = `upi://pay?pa=${publicSettings?.merchant_upi_id}&pn=${encodeURIComponent(publicSettings?.merchant_upi_name || "K Dental")}&am=${amount}&cu=INR&tn=${encodeURIComponent("Order at K Dental")}`;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      window.location.href = upiLink;
    }
    setPhase("verifying");
  };

  const handleFinalConfirm = () => {
    if (!/^[0-9]{12}$/.test(utr)) return;
    onVerifyAndPay(upiId, utr);
  };

  return (
    <div className="custom-rzp-overlay">
      <div className="custom-rzp-modal single-option">
        <div className="rzp-left-panel">
          <div className="rzp-brand">
            <div className="rzp-logo-box">K</div>
            <div className="rzp-brand-name">K Dental</div>
          </div>

          <div className="rzp-price-card">
            <div className="rzp-price-label">Price Summary</div>
            <div className="rzp-price-amount">₹{amount.toLocaleString()}</div>
          </div>

          <div className="rzp-contact-card">
            <span style={{opacity: 0.7}}><Icon name="user" size={14} /></span>
            <span>+91 {contact || "User"}</span>
          </div>

          <div className="rzp-illus"></div>
        </div>

        <div className="rzp-right-panel">
          <div className="rzp-header">
            <div className="rzp-header-title">Payment Options</div>
            <button className="rzp-close-btn" onClick={onClose}>
              <Icon name="close" size={18} />
            </button>
          </div>

          <div className="rzp-content">
            {phase === "input" ? (
              <>
                <div className="rzp-content-header">
                  <span>Pay with UPI QR</span>
                  <span className="rzp-timer">
                     <Icon name="clock" size={12} style={{marginRight: 4}} /> {formatTime(timeLeft)}
                  </span>
                </div>

                <div className="rzp-qr-section">
                  <div className="rzp-qr-box">
                    {publicSettings?.merchant_upi_id ? (
                      <QRCodeCanvas 
                          value={`upi://pay?pa=${publicSettings.merchant_upi_id}&pn=${encodeURIComponent(publicSettings.merchant_upi_name || "K Dental")}&am=${amount}&cu=INR&tn=${encodeURIComponent("Order at K Dental")}`}
                          size={256}
                          level={"H"}
                          style={{ width: "100%", height: "auto" }}
                          includeMargin={false}
                      />
                    ) : (
                      <div style={{padding: 20, fontSize: 12}}>Loading...</div>
                    )}
                  </div>
                  
                  <div className="rzp-qr-hint">
                    <div className="rzp-qr-hint-text">
                          Paying {publicSettings?.merchant_upi_name || "Merchant"}
                    </div>
                    <div className="rzp-upi-apps">
                      <div className="rzp-app-badge" title="Google Pay">
                        <img src={gpayLogo} alt="GPay" />
                      </div>
                      <div className="rzp-app-badge" title="PhonePe">
                        <img src={phonepeLogo} alt="PhonePe" />
                      </div>
                      <div className="rzp-app-badge" title="Paytm">
                        <img src={paytmLogo} alt="Paytm" />
                      </div>
                      <div className="rzp-app-badge" title="BHIM">
                        <img src={bhimLogo} alt="BHIM" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rzp-divider" />

                <div className="rzp-content-footer">
                  <div style={{fontSize: 13, color: "#374151", marginBottom: 12, fontWeight: 600}}>Enter UPI ID / Number</div>
                  <input 
                    type="text" 
                    className="rzp-input" 
                    placeholder="yourname@upi" 
                    value={upiId}
                    onChange={e => setUpiId(e.target.value)}
                  />
                  
                  <button className="rzp-verify-btn" onClick={handleInitiate}>
                    Verify and Pay
                  </button>
                </div>
              </>
            ) : (
              <div className="rzp-utr-phase">
                <div className="rzp-verifying-header">
                  <div className="rzp-pulse-icon">
                    <Icon name="clock" size={32} />
                  </div>
                  <h3>Wait for Confirmation</h3>
                  <p>Finish payment in your UPI app. Then, enter the <strong>12-digit Ref No.</strong> from your payment screenshot below.</p>
                </div>

                <div className="rzp-utr-input-box">
                  <label>12-Digit Ref No. (UTR)</label>
                  <input 
                    type="text" 
                    className="rzp-input utr-field" 
                    placeholder="Enter 12 digits here" 
                    maxLength={12}
                    value={utr}
                    onChange={e => setUtr(e.target.value.replace(/\D/g, ''))}
                  />
                  <div className="utr-hint">Example: 234567890123</div>
                </div>

                <button 
                  className={`rzp-verify-btn confirm-btn ${utr.length === 12 ? 'active' : 'disabled'}`}
                  onClick={handleFinalConfirm}
                  disabled={utr.length !== 12}
                >
                  {utr.length === 12 ? "Confirm Order" : "Enter 12 digits to continue"}
                </button>

                <button className="rzp-back-btn" onClick={() => setPhase('input')}>
                   Go Back
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
